import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  computed,
  inject,
  signal
} from '@angular/core';
import { MsgService } from '../../service/msg.service';
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";

interface JsonToken {
  valor: string;
  classe: string;
}

@Component({
  selector: 'app-json-editor',
  standalone: true,
  imports: [MatIcon, MatTooltip],
  templateUrl: './json-editor.component.html',
  styleUrl: './json-editor.component.css'
})
export class JsonEditorComponent implements OnChanges {

  private readonly msgService = inject(MsgService);

  @ViewChild('editor') private editor?: ElementRef<HTMLTextAreaElement>;

  @Input() isEditavel = false;
  @Input() label = 'JSON';
  @Input() value?: string = '';
  @Input() altura = '';
  @Input() autoAjustarAltura = false;
  @Input() placeholder = '{\n  \n}';

  @Output() changeValue = new EventEmitter<string>();
  @Output() validoChange = new EventEmitter<boolean>();

  readonly texto = signal<string>('');
  readonly erro = signal<string | null>(null);

  readonly linhasNumeros = computed(() => {
    const total = this.texto().split('\n').length || 1;
    return Array.from({ length: total }, (_, i) => i + 1);
  });

  readonly linhasTokenizadas = computed(() => {
    if (this.isEditavel) return [];
    return this.texto().split('\n').map(l => this.tokenizarLinha(l));
  });

  private cursorDefinido = false;

  ngOnChanges(changes: SimpleChanges) {
    if (changes['value']) {
      const novo = this.value ?? '';
      if (novo !== this.texto()) {
        this.texto.set(novo);
        if (this.isEditavel) {
          this.validar(false);
        }
      }
    }
  }

  aoDigitar(event: Event) {
    const alvo = event.target as HTMLTextAreaElement;
    this.atualizarTexto(alvo.value);
    this.registrarCursor(event);
  }

  aoPressionarTecla(event: KeyboardEvent) {
    const textarea = event.target as HTMLTextAreaElement;
    const inicio = textarea.selectionStart;
    const fim = textarea.selectionEnd;
    const valor = textarea.value;

    if (event.key === 'Tab') {
      event.preventDefault();
      const novoValor = valor.substring(0, inicio) + '  ' + valor.substring(fim);
      textarea.value = novoValor;
      textarea.selectionStart = textarea.selectionEnd = inicio + 2;
      this.cursorDefinido = true;
      this.atualizarTexto(novoValor);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const inicioLinha = valor.lastIndexOf('\n', inicio - 1) + 1;
      const linhaAtual = valor.slice(inicioLinha, inicio);
      const indentAtual = (linhaAtual.match(/^[ \t]*/) ?? [''])[0];

      const anterior = valor.slice(0, inicio).replace(/[ \t]*$/, '');
      const abriuBloco = anterior.endsWith('{') || anterior.endsWith('[');
      const indent = indentAtual + (abriuBloco ? '  ' : '');

      const insercao = '\n' + indent;
      const novoValor = valor.substring(0, inicio) + insercao + valor.substring(fim);
      textarea.value = novoValor;
      const pos = inicio + insercao.length;
      textarea.selectionStart = textarea.selectionEnd = pos;
      this.cursorDefinido = true;
      this.atualizarTexto(novoValor);
    }
  }

  registrarCursor(event: Event) {
    const textarea = event.target as HTMLTextAreaElement;
    this.cursorDefinido = textarea.selectionStart !== null;
  }

  inserirNoCursor(valor: string): boolean {
    const textarea = this.editor?.nativeElement;
    if (!textarea || !this.cursorDefinido) return false;

    const inicio = textarea.selectionStart;
    const fim = textarea.selectionEnd;
    const textoAtual = this.texto();
    if (!this.cursorEstaEmValorTextual(textoAtual, inicio)) return false;

    const novoTexto = textoAtual.slice(0, inicio) + valor + textoAtual.slice(fim);
    const novaPosicao = inicio + valor.length;
    textarea.value = novoTexto;
    this.atualizarTexto(novoTexto);

    queueMicrotask(() => {
      textarea.focus();
      textarea.setSelectionRange(novaPosicao, novaPosicao);
      this.cursorDefinido = true;
    });
    return true;
  }

  obterAltura(): string {
    if (!this.autoAjustarAltura) return this.altura;

    const alturaMinima = Number.parseInt(this.altura, 10) || 320;
    const alturaPelasLinhas = this.linhasNumeros().length * 20 + 90;
    return `${Math.max(alturaMinima, alturaPelasLinhas)}px`;
  }

  private atualizarTexto(novoValor: string) {
    this.texto.set(novoValor);
    this.validar(false);
    this.changeValue.emit(novoValor);
  }

  private cursorEstaEmValorTextual(texto: string, posicao: number): boolean {
    let dentroDeString = false;
    let caractereEscapado = false;

    for (let indice = 0; indice < posicao; indice++) {
      const caractere = texto[indice];
      if (caractereEscapado) {
        caractereEscapado = false;
        continue;
      }
      if (caractere === '\\') {
        caractereEscapado = true;
        continue;
      }
      if (caractere === '"') {
        dentroDeString = !dentroDeString;
      }
    }
    if (!dentroDeString) return false;

    caractereEscapado = false;
    for (let indice = posicao; indice < texto.length; indice++) {
      const caractere = texto[indice];
      if (caractereEscapado) {
        caractereEscapado = false;
        continue;
      }
      if (caractere === '\\') {
        caractereEscapado = true;
        continue;
      }
      if (caractere !== '"') continue;

      const proximoCaractere = texto.slice(indice + 1).trimStart()[0];
      return proximoCaractere !== ':';
    }
    return true;
  }

  sincronizarScroll(event: Event, gutter: HTMLElement) {
    gutter.scrollTop = (event.target as HTMLElement).scrollTop;
  }

  formatar() {
    const conteudo = this.texto().trim();
    if (!conteudo) return;

    try {
      const formatado = JSON.stringify(JSON.parse(conteudo), null, 2);
      this.texto.set(formatado);
      this.erro.set(null);
      this.changeValue.emit(formatado);
      this.validoChange.emit(true);
    } catch {
      this.erro.set('JSON inválido: não foi possível formatar.');
      this.validoChange.emit(false);
    }
  }

  copiar() {
    navigator.clipboard.writeText(this.texto())
      .then(() => this.msgService.msgSucesso('JSON copiado'))
      .catch(() => this.msgService.msgAdvertencia('Não foi possível copiar'));
  }

  validar(emitirMensagem = true): boolean {
    const conteudo = this.texto().trim();
    if (!conteudo) {
      this.erro.set(null);
      this.validoChange.emit(true);
      return true;
    }
    try {
      JSON.parse(conteudo);
      this.erro.set(null);
      this.validoChange.emit(true);
      return true;
    } catch {
      const msg = 'O conteúdo informado não é um JSON válido.';
      this.erro.set(msg);
      if (emitirMensagem) {
        this.msgService.msgAdvertencia(msg);
      }
      this.validoChange.emit(false);
      return false;
    }
  }

  private tokenizarLinha(linha: string): JsonToken[] {
    const tokens: JsonToken[] = []
    const re = /("(?:[^"\\]|\\.)*")(\s*:)?|(-?\d+(?:\.\d+)?)|\b(true|false|null)\b/g;

    let ultimo = 0;
    let m: RegExpExecArray | null;

    while ((m = re.exec(linha)) !== null) {
      if (m.index > ultimo) {
        tokens.push({ valor: linha.slice(ultimo, m.index), classe: 'json-pont' });
      }
      if (m[1] !== undefined) {
        if (m[2]) {
          tokens.push({ valor: m[1], classe: 'json-chave' });
          tokens.push({ valor: m[2], classe: 'json-pont' });
        } else {
          tokens.push({ valor: m[1], classe: 'json-string' });
        }
      } else if (m[3] !== undefined) {
        tokens.push({ valor: m[3], classe: 'json-num' });
      } else if (m[4] !== undefined) {
        tokens.push({ valor: m[4], classe: 'json-bool' });
      }
      ultimo = re.lastIndex;
    }

    if (ultimo < linha.length) {
      tokens.push({ valor: linha.slice(ultimo), classe: 'json-pont' });
    }
    return tokens;
  }
}
